/* ===== Audit Log Page ===== */

import { useEffect, useState } from 'react'
import type { AuditLog } from '@zetyar/domain'
import { ShieldAlert } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Badge } from '@/presentation/components/ui/Badge'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webAuditRepository } from '@/infrastructure/repositories/audit'

const TARGET_TYPE_LABEL: Record<string, string> = {
  membership: 'Membresia',
  invitation: 'Invitacion',
  announcement: 'Comunicado',
  channel: 'Canal',
  chat_message: 'Mensaje',
  document: 'Documento',
  event: 'Actividad',
  event_registration: 'Inscripcion',
  project: 'Proyecto',
  supply_product: 'Insumo',
  supply_offering: 'Oferta de insumo',
  supply_reservation: 'Reserva de insumo',
  campaign: 'Campaña',
  finance_category: 'Categoria financiera',
  income_record: 'Ingreso',
  expense_record: 'Egreso',
}

export function AuditPage() {
  const { currentMembership, currentOrganization, profile, permissions, user } = useApp()
  const canView = profile?.isSuperAdmin || permissions.includes('audit.view')
  const [entries, setEntries] = useState<AuditLog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true

    const load = async () => {
      if (!currentOrganization || !profile || !user || !canView) {
        setEntries([])
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setLoadError(null)
        const result = await webAuditRepository.list(
          createWebTenantContext({
            authUid: user.uid,
            personId: profile.id,
            organizationId: currentOrganization.id,
            membershipId: currentMembership?.id,
          }),
          { limit: 100 },
        )
        if (isMounted) setEntries(result)
      } catch (error) {
        console.error('Error al cargar la auditoria:', error)
        if (isMounted) {
          setEntries([])
          setLoadError('No fue posible cargar el registro de auditoria de esta organizacion.')
        }
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    void load()

    return () => {
      isMounted = false
    }
  }, [currentMembership, currentOrganization, profile, user, canView])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Auditoria</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
          Registro inmutable de acciones sensibles en {currentOrganization?.name}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-primary-600" /> Acciones registradas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!canView ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Tu rol no tiene permisos para ver la auditoria de esta organizacion.
            </p>
          ) : isLoading ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 animate-pulse">Cargando auditoria...</p>
          ) : loadError ? (
            <p className="text-sm text-danger-600">{loadError}</p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">No hay acciones registradas todavia.</p>
          ) : (
            <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {entries.map((entry) => (
                <div key={entry.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                      {entry.action}
                      {entry.detail && <span className="text-neutral-500 dark:text-neutral-400"> &middot; {entry.detail}</span>}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {entry.actorDisplayName ?? 'Sistema'} &middot; {new Date(entry.createdAt).toLocaleString('es-CL')}
                    </p>
                  </div>
                  <Badge variant="neutral" className="shrink-0">
                    {TARGET_TYPE_LABEL[entry.targetType] ?? entry.targetType}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
